import { createContext, useCallback, useMemo, useState } from "react";
import { CSSTransition } from "react-transition-group";
import styles from "./Snackbar.module.css";

// Snackbar default values
export const defaultPosition = "bottom-center";
export const defaultDuration = 5000;
export const defaultInterval = 250;
export const positions = [
  "top-left",
  "top-center",
  "top-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
];

export interface ISnackbarContext {
  openSnackbar: (text: string, className?: string, duration?: number) => void;
  closeSnackbar: () => void;
}
// Context used by the hook useSnackbar() and HoC withSnackbar()
export const SnackbarContext = createContext<ISnackbarContext>(null);

export default function SnackbarProvider({ children }) {
  // Current open state
  const [open, setOpen] = useState<boolean>(false);
  // Current timeout ID
  const [timeoutId, setTimeoutId] = useState(null);
  // Snackbar's text
  const [text, setText] = useState<string>("");
  // Snackbar's duration
  const [duration, setDuration] = useState<number>(defaultDuration);
  // Snackbar's position
  const [position] = useState<string>(defaultPosition);

  const [className, setClassName] = useState<string>("");

  const triggerSnackbar = (
    text: string,
    duration: number,
    className: string
  ) => {
    setText(text);
    setDuration(duration);
    setOpen(true);
    setClassName(className)
  };

  // Manages all the snackbar's opening process
  const openSnackbar = useCallback(
    (
      text: string,
      className: string,
      duration: number
    ) => {
      // Closes the snackbar if it is already open
      if (open) {
        setOpen(false);
        setTimeout(() => {
          triggerSnackbar(text, duration, className);
        }, defaultInterval);
      } else {
        triggerSnackbar(text, duration, className);
      }
    },
    [open]
  );

  // Closes the snackbar just by setting the "open" state to false
  const closeSnackbar = useCallback(() => {
    setOpen(false);
  }, []);

  // Returns the Provider that must wrap the application

  const providerContext = useMemo(
    () => ({
      openSnackbar,
      closeSnackbar,
    }),
    [closeSnackbar, openSnackbar]
  );
  return (
    <SnackbarContext.Provider value={providerContext}>
      {children}

      {/* Renders Snackbar on the end of the page */}
      <CSSTransition
        in={open}
        timeout={150}
        mountOnEnter
        unmountOnExit
        // Sets timeout to close the snackbar
        onEnter={() => {
          clearTimeout(timeoutId);
          setTimeoutId(setTimeout(() => setOpen(false), duration));
        }}
        // Sets custom classNames based on "position"
        className={`${styles["snackbar-wrapper"]} ${
          styles[`snackbar-wrapper-${position}`]
        }`}
        classNames={{
          enter: `${styles["snackbar-enter"]} ${
            styles[`snackbar-enter-${position}`]
          }`,
          enterActive: `${styles["snackbar-enter-active"]} ${
            styles[`snackbar-enter-active-${position}`]
          }`,
          exitActive: `${styles["snackbar-exit-active"]} ${
            styles[`snackbar-exit-active-${position}`]
          }`,
        }}
      >
        {/* This div will be rendered with CSSTransition classNames */}
        <div>
          <div className={`${styles.snackbar} snackbar ${className}`}>
            {/* Snackbar's text */}
            <div className={styles.snackbar__text}>{text}</div>

            {/* Snackbar's close button */}
            <button onClick={closeSnackbar} className={styles.snackbar__close}>
              <i className='material-icons'>close</i>
            </button>
          </div>
        </div>
      </CSSTransition>
    </SnackbarContext.Provider>
  );
}
